import { get, set } from "idb-keyval";

import { dbStorage } from "../utils/dbStorage";

// Types
export interface Deployment {
  deployment_id: string;
  projectSlug: string;
  build_url: string;
  createdAt: string;
  githubUsername: string;
  isActive: boolean;
  message: string;
  orgId: string;
  service_url: string;
  custom_domain?: string;
  status: string;
  timestamp: string;
  updatedAt: string;
  userId: string;
}

export interface ProjectMetadata {
  id: string;
  createdAt: number;
  name: string;
  organizationId?: string;
  userId?: string;
  isPublished?: boolean;
  status?: string;
  repository?: {
    name: string;
    fullName: string;
    owner: string;
  };
  framework?: string;
  updatedAt?: number;
  deployments?: Deployment[];
  url?: string;
  deploymentUrl?: string;
  branch?: string;
  icon?: string;
  ownerName?: string;
  ownerImage?: string;
  isQuickStart?: boolean;
}

export interface UIState {
  selectedFile: string | null;
  openFiles: string[];
}

export interface ProjectState {
  metadata: ProjectMetadata;
  uiState: UIState;
  fsSnapshot?: unknown; // Will contain serialized filesystem data
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  code?: string;
  project?: ProjectMetadata;
  deployment?: Deployment;
}

// Constants
const PROJECT_PREFIX = "project:";
const DEFAULT_UI_STATE: UIState = {
  selectedFile: "/project/hello.py",
  openFiles: [],
};

/**
 * Generates a short unique ID that is more readable
 * Format: 4 char random = 4 chars total
 */
function generateUUID(): string {
  // Second part: 4 random alphabetic chars
  const randomPart = Array.from({ length: 4 }, () =>
    String.fromCharCode(Math.floor(Math.random() * 26) + 97) // Random lowercase letter
  ).join('');

  // Return the random part as the ID
  return randomPart;
}

/**
 * ProjectService manages creating, loading, saving, and clearing projects
 * Now uses API calls to manage project metadata, while keeping files in IndexedDB
 */
export class ProjectService {
  private static instance: ProjectService;
  private currentProjectId: string | null = null;
  private currentOrganizationId: string | null = null;
  private currentUserId: string | null = null;
  private apiBaseUrl: string = import.meta.env.VITE_BACKEND_URL || "";
  private token: string | null = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): ProjectService {
    if (!ProjectService.instance) {
      ProjectService.instance = new ProjectService();
    }
    return ProjectService.instance;
  }

  /**
   * Sets the auth token for API requests
   */
  public setToken(token: string): void {
    this.token = token;
  }

  /**
   * Set user and organization context
   */
  public setUserContext(userId: string, organizationId: string | null): void {
    this.currentUserId = userId;
    this.currentOrganizationId = organizationId;
  }

  public getCurrentUserId(): string | null {
    return this.currentUserId;
  }

  public getCurrentOrganizationId(): string | null {
    return this.currentOrganizationId;
  }

  /**
   * Get current project ID
   */
  public getCurrentProjectId(): string | null {
    return this.currentProjectId;
  }

  /**
   * Sets the current project ID
   */
  public setCurrentProjectId(projectId: string | null): void {
    this.currentProjectId = projectId;
  }

  /**
   * Initialize the service with a project from URL if present
   */
  public initializeFromURL(): string | null {
    const url = new URL(window.location.href);
    const projectId = url.searchParams.get("project");
    if (projectId) {
      this.currentProjectId = projectId;
    }
    return projectId;
  }

  /**
   * Create a new project via API and update project cache
   */
  public async createProject(name?: string, type: string = 'blank'): Promise<ProjectMetadata> {
    if (!this.token) {
      throw new Error("Authentication token not available");
    }

    // Generate a default name if none provided
    if (!name) {
      const randomNum = Math.floor(Math.random() * 900) + 100; // Random 3-digit number
      const projectType = type.charAt(0).toUpperCase() + type.slice(1); // Capitalize first letter
      name = `${projectType} Project ${randomNum}`;
    }

    const headers: HeadersInit = {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    };

    if (this.currentOrganizationId) {
      headers['X-Organization-ID'] = this.currentOrganizationId;
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/api/projects`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name,
          userId: this.currentUserId,
          organizationId: this.currentOrganizationId || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || response.statusText);
      }

      const result: ApiResponse<ProjectMetadata> = await response.json();

      if (!result.success || !result.data) {
        throw new Error(result.error || "Failed to create project");
      }

      // Update the project cache in IndexedDB
      try {
        // Get existing projects from cache
        const orgId = this.currentOrganizationId;
        const cachedProjects = await dbStorage.getProjects(orgId);

        // Add new project to the list and update cache
        const updatedProjects = [result.data, ...cachedProjects];
        await dbStorage.storeProjects(updatedProjects, orgId);

        // Notify any listeners
        window.dispatchEvent(new CustomEvent('projectsRefreshed'));
      } catch (cacheError) {
        console.warn("Failed to update project cache after creation:", cacheError);
        // Non-critical error, we can continue
      }

      this.currentProjectId = result.data.id;
      return result.data;
    } catch (error) {
      console.error("Error creating project:", error);
      throw error;
    }
  }

  /**
   * Get a project by ID, with optional caching
   */
  public async getProject(projectId: string): Promise<ProjectMetadata> {
    if (!this.token) {
      throw new Error("Authentication token not available");
    }

    const headers: HeadersInit = {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    };

    if (this.currentOrganizationId) {
      headers['X-Organization-ID'] = this.currentOrganizationId;
    }

    try {
      // Try to get project from cache first
      const orgId = this.currentOrganizationId;
      const cachedProjects = await dbStorage.getProjects(orgId);
      const cachedProject = cachedProjects.find(project => project.id === projectId);

      if (cachedProject) {
        console.log(`Found project ${projectId} in cache`);
        return cachedProject;
      }

      const response = await fetch(`${this.apiBaseUrl}/api/projects/${projectId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let errorMessage = `Server responded with status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || response.statusText;
        } catch (jsonError) {
          // If we can't parse the JSON, just use the statusText
          errorMessage = response.statusText;
        }
        throw new Error(errorMessage);
      }

      const result: ApiResponse<ProjectMetadata> = await response.json();

      if (!result.success || !result.data) {
        throw new Error(result.error || "Failed to get project");
      }

      // Update the individual project in the cache if it exists
      try {
        if (cachedProjects.length > 0) {
          const updatedProjects = cachedProjects.map(project =>
            project.id === projectId ? result.data : project
          );

          // If the project wasn't in the cache, add it
          if (!cachedProjects.some(p => p.id === projectId)) {
            updatedProjects.push(result.data);
          }

          await dbStorage.storeProjects(updatedProjects, orgId);
        }
      } catch (cacheError) {
        console.warn("Failed to update project cache for individual project:", cacheError);
        // Non-critical error, we can continue
      }

      return result.data;
    } catch (error) {
      // Enhance error message with network information
      let errorMessage = error instanceof Error ? error.message : String(error);

      // Check for specific error types
      if (error instanceof TypeError && errorMessage.includes("Failed to fetch")) {
        errorMessage = `Network error: Failed to connect to server. Please check your connection and try again.`;
      } else if (error instanceof DOMException && error.name === "AbortError") {
        errorMessage = "Request timed out. Please try again later.";
      }

      console.error(`Error getting project ${projectId}:`, errorMessage);
      throw new Error(errorMessage);
    }
  }

  /**
   * Update a project and update cache
   */
  public async updateProject(projectId: string, updates: Partial<ProjectMetadata>): Promise<ProjectMetadata> {
    if (!this.token) {
      throw new Error("Authentication token not available");
    }

    const headers: HeadersInit = {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    };

    if (this.currentOrganizationId) {
      headers['X-Organization-ID'] = this.currentOrganizationId;
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/api/projects/${projectId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || response.statusText);
      }

      const result: ApiResponse<ProjectMetadata> = await response.json();

      if (!result.success || !result.data) {
        throw new Error(result.error || "Failed to update project");
      }

      // Update the project in cache
      try {
        const orgId = this.currentOrganizationId;
        const cachedProjects = await dbStorage.getProjects(orgId);

        // Update the project in the list
        const updatedProjects = cachedProjects.map(project =>
          project.id === projectId ? result.data : project
        );

        await dbStorage.storeProjects(updatedProjects, orgId);
        window.dispatchEvent(new CustomEvent('projectsRefreshed'));
      } catch (cacheError) {
        console.warn("Failed to update project cache after update:", cacheError);
        // Non-critical error, continue
      }

      return result.data;
    } catch (error) {
      console.error(`Error updating project ${projectId}:`, error);
      throw error;
    }
  }

  public async duplicateProject(projectId: string): Promise<ProjectMetadata> {
    if (!this.token) {
      throw new Error("Authentication token not available");
    }

    try {
      const originalProject = await this.getProject(projectId);

      const duplicatedName = `${originalProject.name} (Copy)`;
      const newProject = await this.createProject(duplicatedName, 'blank');

      try {
        const originalProjectKey = `${PROJECT_PREFIX}${projectId}`;
        const newProjectKey = `${PROJECT_PREFIX}${newProject.id}`;

        const originalProjectState = (await get(originalProjectKey)) as ProjectState | undefined;

        if (originalProjectState?.fsSnapshot) {
          const newProjectState: ProjectState = {
            metadata: newProject,
            uiState: { selectedFile: null, openFiles: [] },
            fsSnapshot: originalProjectState.fsSnapshot
          };

          await set(newProjectKey, newProjectState);
        }

        const helloPyContent = await dbStorage.getHelloPyContent(projectId);
        if (helloPyContent) {
          await dbStorage.storeHelloPyContent(newProject.id, helloPyContent);
        }
      } catch (fileError) {
        console.warn("Failed to copy project files:", fileError);
      }

      return newProject;
    } catch (error) {
      console.error(`Error duplicating project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a project and update the cache
   */
  public async deleteProject(projectId: string): Promise<boolean> {
    if (!this.token) {
      throw new Error("Authentication token not available");
    }

    const headers: HeadersInit = {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    };

    if (this.currentOrganizationId) {
      headers['X-Organization-ID'] = this.currentOrganizationId;
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || response.statusText);
      }

      const result: ApiResponse<{ deleted: boolean }> = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to delete project");
      }

      if (this.currentProjectId === projectId) {
        this.currentProjectId = null;
      }

      // Remove the project from cache
      try {
        const orgId = this.currentOrganizationId;
        const cachedProjects = await dbStorage.getProjects(orgId);

        // Filter out the deleted project
        const updatedProjects = cachedProjects.filter(project => project.id !== projectId);

        await dbStorage.storeProjects(updatedProjects, orgId);
        window.dispatchEvent(new CustomEvent('projectsRefreshed'));
      } catch (cacheError) {
        console.warn("Failed to update project cache after deletion:", cacheError);
        // Non-critical error, continue
      }

      return true;
    } catch (error) {
      console.error(`Error deleting project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * List all projects for the current user/organization
   * Uses cache when available and not stale
   */
  public async listProjects(filter?: string): Promise<ProjectMetadata[]> {
    if (!this.token) {
      throw new Error("Authentication token not available");
    }

    const headers: HeadersInit = {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    };

    if (this.currentOrganizationId) {
      headers['X-Organization-ID'] = this.currentOrganizationId;
    }

    const orgId = this.currentOrganizationId;

    try {
      // Check if we can use the cached data
      const shouldRefresh = await dbStorage.shouldRefreshProjects(orgId);

      if (!shouldRefresh) {
        console.log("Using cached projects from IndexedDB");
        const cachedProjects = await dbStorage.getProjects(orgId);

        if (cachedProjects.length > 0) {
          // Apply filter if needed
          if (filter) {
            if (filter === 'published') {
              return cachedProjects.filter(project => project.isPublished);
            } else if (filter === 'drafts') {
              return cachedProjects.filter(project => !project.isPublished);
            }
          }
          return cachedProjects;
        }
      }

      // If no cache or stale, fetch from API
      let url = `${this.apiBaseUrl}/api/projects`;

      // Add filter query param if provided
      if (filter) {
        url += `?filter=${filter}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let errorMessage = `Server responded with status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || response.statusText;
        } catch (jsonError) {
          // If we can't parse the JSON, just use the statusText
          errorMessage = response.statusText;
        }
        throw new Error(errorMessage);
      }

      const result: ApiResponse<ProjectMetadata[]> = await response.json();

      if (!result.success || !result.data) {
        throw new Error(result.error || "Failed to list projects");
      }

      // Store projects in cache
      try {
        await dbStorage.storeProjects(result.data, orgId);
        console.log(`Cached ${result.data.length} projects to IndexedDB`);
      } catch (cacheError) {
        console.warn("Failed to cache projects:", cacheError);
        // Non-critical error, continue
      }

      return result.data;
    } catch (error) {
      // Enhance error message with network information
      let errorMessage = error instanceof Error ? error.message : String(error);

      // Check for specific error types
      if (error instanceof TypeError && errorMessage.includes("Failed to fetch")) {
        errorMessage = `Network error: Failed to connect to server. Please check your connection and try again.`;
      }

      // Try to use cached data even if it's stale when there's a network error
      if (errorMessage.includes("Network error") || errorMessage.includes("Failed to connect")) {
        try {
          console.log("Network error, trying to use cached projects");
          const cachedProjects = await dbStorage.getProjects(orgId);

          if (cachedProjects.length > 0) {
            console.log("Using stale cached projects due to network error");
            // Apply filter if needed
            if (filter) {
              if (filter === 'published') {
                return cachedProjects.filter(project => project.isPublished);
              } else if (filter === 'drafts') {
                return cachedProjects.filter(project => !project.isPublished);
              }
            }
            return cachedProjects;
          }
        } catch (fallbackError) {
          console.error("Error falling back to cached projects:", fallbackError);
          // Continue to throw the original error
        }
      }

      console.error("Error listing projects:", errorMessage);
      throw new Error(errorMessage);
    }
  }

  /**
   * Update project and cache after deployment
   * Based on the /deploy endpoint response format
   */
  public async processDeploymentResult(deploymentResponse: any): Promise<{ project: ProjectMetadata, deployment: Deployment | null }> {
    try {
      if (!deploymentResponse.success) {
        throw new Error(deploymentResponse.error || "Deployment failed");
      }

      let project: ProjectMetadata;
      let deployment: Deployment | null = null;

      if (deploymentResponse.project && deploymentResponse.deployment) {
        project = deploymentResponse.project;
        deployment = deploymentResponse.deployment;
      } else if (this.currentProjectId) {
        const currentProject = await this.getProject(this.currentProjectId);

        project = {
          ...currentProject,
          isPublished: true,
          status: "published",
          deploymentUrl: deploymentResponse.website_url || deploymentResponse.url || deploymentResponse.service_url,
          updatedAt: Date.now()
        };

        if (deploymentResponse.deployment_id || deploymentResponse.website_url) {
          deployment = {
            deployment_id: deploymentResponse.deployment_id || `dep_${Date.now()}`,
            projectSlug: currentProject.name.toLowerCase().replace(/[^a-z0-9]/g, '-'),
            build_url: deploymentResponse.build_url || "",
            createdAt: new Date().toISOString(),
            githubUsername: "",
            isActive: true,
            message: "Deployment successful",
            orgId: this.currentOrganizationId || "",
            service_url: deploymentResponse.website_url || deploymentResponse.url || "",
            custom_domain: deploymentResponse.custom_domain,
            status: "success",
            timestamp: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            userId: this.currentUserId || ""
          };
        }
      } else {
        throw new Error("missing project data and no current project ID");
      }

      // Update the project in cache with new deployment info
      try {
        const orgId = this.currentOrganizationId;
        const cachedProjects = await dbStorage.getProjects(orgId);

        if (cachedProjects.length > 0) {
          // Find and update the deployed project
          const updatedProjects = cachedProjects.map(cachedProject => {
            if (cachedProject.id === project.id) {
              // Ensure the project has all the updated fields including deployments
              const updatedProject = {
                ...cachedProject,
                ...project,
                isPublished: true,
                deploymentUrl: project.deploymentUrl || deployment?.service_url || deployment?.custom_domain,
                status: "published",
                updatedAt: Date.now()
              };

              if (deployment) {
                const existingDeployments = cachedProject.deployments || [];
                updatedProject.deployments = [deployment, ...existingDeployments];
              }

              return updatedProject;
            }
            return cachedProject;
          });

          await dbStorage.storeProjects(updatedProjects, orgId);
          window.dispatchEvent(new CustomEvent('projectsRefreshed'));
        }
      } catch (cacheError) {
        console.warn("Failed to update project cache after deployment:", cacheError);
        // Non-critical error, continue
      }

      return { project, deployment };
    } catch (error) {
      console.error("Error processing deployment result:", error);
      throw error;
    }
  }

  /**
   * Process deployment updates from streaming response
   * Updates the project cache when deployment is complete
   */
  public async processDeploymentUpdates(updates: any[], finalUrl: string): Promise<void> {
    if (!updates || updates.length === 0) {
      return;
    }

    try {
      // Find the last non-error update which should contain the most up-to-date information
      const lastUpdate = [...updates].reverse().find(update => update.success !== false);

      if (!lastUpdate) {
        console.warn("No successful deployment updates found");
        return;
      }

      // Check if the final update has project information
      if (lastUpdate.project) {
        const projectId = lastUpdate.project.id;

        // Get the project info
        const orgId = this.currentOrganizationId;
        const cachedProjects = await dbStorage.getProjects(orgId);

        // Find and update the project in cache
        if (cachedProjects.length > 0) {
          const updatedProjects = cachedProjects.map(project => {
            if (project.id === projectId) {
              // Create an updated project with deployment information
              const updatedProject = {
                ...project,
                ...lastUpdate.project,
                isPublished: true,
                deploymentUrl: finalUrl || lastUpdate.project.deploymentUrl,
                status: "published",
              };

              // Add the deployment information if available
              if (lastUpdate.deployment) {
                const deployments = project.deployments || [];
                updatedProject.deployments = [lastUpdate.deployment, ...deployments];
              }

              return updatedProject;
            }
            return project;
          });

          await dbStorage.storeProjects(updatedProjects, orgId);
          window.dispatchEvent(new CustomEvent('projectsRefreshed'));
        }
      }
    } catch (error) {
      console.warn("Error processing deployment updates for cache:", error);
      // Non-critical, continue
    }
  }

  /**
   * Save filesystem snapshot to current project
   * This still uses IndexedDB as files are managed locally
   */
  public async saveFilesystemSnapshot(fsSnapshot: unknown): Promise<void> {
    if (!this.currentProjectId) return;

    try {
      const key = `${PROJECT_PREFIX}${this.currentProjectId}`;
      const projectState = (await get(key)) as ProjectState | undefined;

      if (projectState) {
        const updatedState: ProjectState = {
          ...projectState,
          fsSnapshot,
        };

        await set(key, updatedState);
      }
    } catch (error) {
      console.error("Error saving filesystem snapshot:", error);
    }
  }

  /**
   * Load filesystem snapshot for current project
   * This still uses IndexedDB as files are managed locally
   */
  public async loadFilesystemSnapshot(projectId: string): Promise<unknown | null> {
    try {
      const key = `${PROJECT_PREFIX}${projectId}`;
      const projectState = (await get(key)) as ProjectState | undefined;
      return projectState?.fsSnapshot || null;
    } catch (error) {
      console.error("Error loading filesystem snapshot:", error);
      return null;
    }
  }

  /**
   * Fetch projects and update cache
   */
  public async fetchAndCacheProjects(forceRefresh = false): Promise<ProjectMetadata[] | undefined> {
    try {
      if (!this.token) {
        const tokenFromSession = sessionStorage.getItem('clerk-auth-token');
        if (tokenFromSession) {
          this.setToken(tokenFromSession);
        } else {
          throw new Error("No auth token");
        }
      }

      const orgId = this.getCurrentOrganizationId();
      const needsRefresh = forceRefresh || (await dbStorage.shouldRefreshProjects(orgId));

      if (!needsRefresh) {
        window.dispatchEvent(new CustomEvent("projectsRefreshed"));
        return await dbStorage.getProjects(orgId);
      }

      const projects = await this.listProjects();
      await dbStorage.storeProjects(projects, orgId);

      setTimeout(
        () => window.dispatchEvent(new CustomEvent("projectsRefreshed")),
        100
      );

      return projects;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error(`Error caching projects: ${msg}`);
      throw e;
    }
  }
}

// Export a singleton instance
export const projectService = ProjectService.getInstance();